import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  HostListener,
  Input,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SlideshowService } from '../../services/slideshow.service';
import { FavoritesService } from '../../services/favorites.service';
import { SeenPersistenceService } from '../../services/seen-persistence.service';
import { AutoLoopService } from '../../services/auto-loop.service';
import { ToastService } from '../../services/toast.service';
import { VolumeService } from '../../services/volume.service';
import { FullscreenService } from '../../services/fullscreen.service';
import { MediaPreloadService } from '../../services/media-preload.service';
import { VideoPost } from '../../models/e621.models';
import { SearchComponent } from '../search/search.component';
import { FavoriteButtonComponent } from '../favorite-button/favorite-button.component';
import { TagsComponent } from '../tags/tags.component';

@Component({
  selector: 'app-video-player',
  standalone: true,
  imports: [CommonModule, FavoriteButtonComponent, TagsComponent],
  templateUrl: './video-player.component.html',
  styleUrl: './video-player.component.scss',
})
export class VideoPlayerComponent implements OnInit, OnDestroy {
  @ViewChild('videoElement', { static: false })
  videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('imageElement', { static: false })
  imageElement!: ElementRef<HTMLImageElement>;
  @Input() searchComponent!: SearchComponent;
  private slideshowService = inject(SlideshowService);
  private favoritesService = inject(FavoritesService);
  private seenPersistenceService = inject(SeenPersistenceService);
  private autoLoopService = inject(AutoLoopService);
  private http = inject(HttpClient);
  private sanitizer = inject(DomSanitizer);
  private toastService = inject(ToastService);
  private volumeService = inject(VolumeService);
  private fullscreenService = inject(FullscreenService);
  private mediaPreloadService = inject(MediaPreloadService);
  currentVideo: VideoPost | null = null;
  isLoading = false;
  position = { current: 0, total: 0 };
  error: string | null = null;
  isPlaying = false;
  isLoopingCurrent = false;

  // Getter proxies for template binding (delegated to FullscreenService)
  get isFullWindowMode(): boolean {
    return this.fullscreenService.isFullWindowMode;
  }
  set isFullWindowMode(value: boolean) {
    this.fullscreenService.isFullWindowMode = value;
  }
  get isNativeFullscreen(): boolean {
    return this.fullscreenService.isNativeFullscreen;
  }

  // Auto-loop state
  private autoLoopStartTime = 0;
  private autoLoopTotalPlaytime = 0;
  private autoLoopLastCurrentTime = 0;
  private autoLoopTimer: number | null = null;
  private isAutoLoopActive = false;

  // Computed property for loop state
  get shouldLoop(): boolean {
    return this.isLoopingCurrent || this.isAutoLoopActive;
  }

  // Controls visibility
  showControls = true;
  showHtml5Controls = true; // Separate control for HTML5 video controls
  miniControlsMinimized = false; // Whether fullscreen mini controls are collapsed

  // Get fullscreen controls position from search component
  get fullscreenControlsPosition(): string {
    return this.searchComponent?.getFullscreenControlsPosition() || 'bottom-right';
  }

  // Date navigation proxies (delegated to SearchComponent)
  get hasDateNavigation(): boolean {
    const mode = this.searchComponent?.advancedDateMode;
    return !!mode && mode !== 'none';
  }
  get canShiftDateLater(): boolean {
    return this.searchComponent?.canShiftLater ?? false;
  }
  get activeDateLabel(): string {
    if (!this.searchComponent) return '';
    switch (this.searchComponent.advancedDateMode) {
      case 'period': return this.searchComponent.periodOffsetLabel;
      case 'exact':  return this.searchComponent.exactDateLabel;
      case 'range':  return this.searchComponent.rangeDateLabel;
      default:       return '';
    }
  }
  shiftDateEarlier(): void {
    this.searchComponent?.shiftDatePeriod('prev');
  }
  shiftDateLater(): void {
    this.searchComponent?.shiftDatePeriod('next');
  }

  // Favorites
  isFavorited = false;
  favoritesEnabled = false;
  isFavoriteLoading = false;

  // Touch/swipe handling for full window mode
  private touchStartY = 0;
  private touchStartX = 0;
  private minSwipeDistance = 50;

  // Pinch-to-zoom state for images
  imageScale = 1;
  imageTranslateX = 0;
  imageTranslateY = 0;
  private isPinching = false;
  private pinchStartDist = 0;
  private pinchStartScale = 1;
  private isPanning = false;
  private panStartX = 0;
  private panStartY = 0;
  private panStartTranslateX = 0;
  private panStartTranslateY = 0;
  private readonly MAX_ZOOM_SCALE = 5;
  private readonly ZOOM_SNAP_THRESHOLD = 1.05; // Snap back to 1× if scale falls below this on release
  private pinchCooldown = false;
  private pinchCooldownTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly PINCH_COOLDOWN_MS = 300;

  // Double-click handling for favorite action in fullscreen
  private lastClickTime = 0;
  private readonly DOUBLE_CLICK_DELAY = 300; // 300ms for double-click detection

  private destroy$ = new Subject<void>();

  // Mobile detection
  isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);  // Track if user has interacted with the page (for mobile autoplay)
  private hasUserInteracted = false;
  // Track if we should auto-play videos (separate from slideshow service state)
  private shouldAutoPlay = false;

  // Image support
  private imageTimer: number | null = null;
  private readonly DEFAULT_IMAGE_DURATION = 5000; // 5 seconds default for static images
  imageCountdownSeconds: number | null = null;
  private imageCountdownInterval: number | null = null;

  // Edge progress bar (fullscreen with controls hidden)
  mediaProgress = 0; // 0..1 progress of whatever is currently on screen
  private imageProgressDuration = 0; // ms; 0 when nothing is timing the image
  private imageProgressStart = 0;
  private progressInterval: number | null = null;
  // Single cadence for the edge progress bar: the ticker interval and the CSS
  // transition on the fill are both driven by this, so the bar glides between
  // samples instead of stepping.
  private readonly PROGRESS_TICK_MS = 100;

  // Mobile debugging
  mobileDebugLogs: string[] = [];
  showMobileDebug = false;
  showMobileDebugPanel = false;
  ngOnInit(): void {
    // Set up mobile debugging if on mobile
    if (this.isMobile) {
      this.setupMobileDebugging();
      this.setupMobileDebugGesture();
      this.debugLog('Mobile device detected - debug console available (3-finger tap to show)');
    }

    // Subscribe to favorites service state
    this.favoritesService.getIsEnabled()
      .pipe(takeUntil(this.destroy$))
      .subscribe(enabled => {
        this.favoritesEnabled = enabled;
        this.updateFavoriteStatus();
      });

    // Subscribe to favorites updates
    this.favoritesService.getFavorites()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateFavoriteStatus();
      });

    // Subscribe to current video
    this.slideshowService      .getCurrentVideo()
      .pipe(takeUntil(this.destroy$))
      .subscribe((video) => {
        this.debugLog(`Video player received new video: ${video?.id}`);

        this.currentVideo = video;
        this.error = null;
        // Preserve the slideshow's play/pause state when navigating between videos
        this.isPlaying = this.slideshowService.getIsPlaying();

        if (video) {
          // Check if this should open in fullscreen mode
          if (this.searchComponent) {
            const fullscreenInfo = this.searchComponent.checkAndResetFullscreenFlag();
            if (fullscreenInfo.shouldOpen) {
              console.log(`Opening video in ${fullscreenInfo.mode} fullscreen mode`);              if (fullscreenInfo.mode === 'windowed') {
                this.fullscreenService.enterFullWindowMode();
              } else if (fullscreenInfo.mode === 'native') {
                // Wait a bit for the video to load, then enter native fullscreen
                setTimeout(() => {
                  this.fullscreenService.enterNativeFullscreen();
                }, 500);
              }
            }
          }

          // Use setTimeout to ensure the video element is available and DOM is updated
          setTimeout(() => this.loadVideo(), 0);

          // Update favorite status for new video
          this.updateFavoriteStatus();
        }
      });

    // Subscribe to loading state
    this.slideshowService
      .getLoadingState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        this.isLoading = loading;
      });

    // Subscribe to position updates
    this.slideshowService
      .getCurrentPosition()
      .pipe(takeUntil(this.destroy$))
      .subscribe((position) => {
        this.position = position;
      });

    // Subscribe to errors
    this.slideshowService
      .getErrors()
      .pipe(takeUntil(this.destroy$))
      .subscribe((error) => {
        this.error = error;
      });

    // Set up fullscreen event listeners via service
    this.fullscreenService.addFullscreenEventListeners();
    this.fullscreenService.setOnFullscreenChangeCallback((isFullscreen: boolean) => {
      if (isFullscreen && this.videoElement) {
        this.fullscreenService.addFullscreenWheelListener(this.videoElement.nativeElement);
      } else if (this.videoElement) {
        this.fullscreenService.removeFullscreenWheelListener(this.videoElement.nativeElement);
      }
    });
    this.fullscreenService.setOnWheelNavigateCallback((direction: 'next' | 'previous') => {
      if (!this.currentVideo) return;
      if (direction === 'next') {
        this.nextVideo();
      } else {
        this.previousVideo();
      }
    });  }
  ngOnDestroy(): void {
    // Clean up fullscreen modes via service
    this.fullscreenService.cleanupBodyClasses();

    // Stop any ongoing preloading and clean up via service
    this.mediaPreloadService.stopPreloading();
    this.mediaPreloadService.cleanupAll();

    // Clean up image timer
    this.stopImageTimer();

    // Clean up pinch cooldown timer
    this.clearPinchCooldown();

    // Clean up auto-loop timer
    this.resetAutoLoop();

    // Remove fullscreen event listeners via service
    this.fullscreenService.removeFullscreenEventListeners();

    this.destroy$.next();
    this.destroy$.complete();
  }  /**
   * Load the current media (video or image)
   */  private loadVideo(): void {
    if (!this.currentVideo) return;

    // Stop any ongoing preloading when loading new media
    this.mediaPreloadService.stopPreloading();

    // Stop any image timer
    this.stopImageTimer();

    // Reset auto-loop state for new media
    this.resetAutoLoop();

    // Reset edge progress bar for new media
    this.resetMediaProgress();

    // Reset zoom state for new media
    this.resetImageZoom();

    console.log(
      'Setting up media for:',
      this.currentVideo.id,
      this.currentVideo.url,
      'Type:',
      this.currentVideo.contentType
    );

    if (this.currentVideo.contentType === 'video') {
      this.loadVideoMedia();
    } else {
      this.loadImageMedia();
    }

    // Clear any previous error
    this.error = null;

    // Start loading state
    this.slideshowService.onVideoLoadStart();
  }

  /**
   * Load video media
   */
  private loadVideoMedia(): void {
    if (!this.videoElement) return;

    const video = this.videoElement.nativeElement;

    // Check if we have a preloaded version of this video
    if (this.currentVideo) {
      this.mediaPreloadService.usePreloadedVideoIfAvailable(this.currentVideo.id);
    }

    // Reset video state
    video.currentTime = 0;
    this.isPlaying = false;

    // Ensure the video element is properly configured for mobile
    video.setAttribute('playsinline', 'true');

    // The template [src] binding will trigger the loading
    // Event handlers in template will handle the rest
  }

  /**
   * Load image media
   */
  private loadImageMedia(): void {
    // Reset playing state
    this.isPlaying = false;

    // Check if we have a preloaded version of this image
    if (this.currentVideo) {
      this.mediaPreloadService.usePreloadedImageIfAvailable(this.currentVideo.id);
    }

    // The template [src] binding will trigger the loading
    // Event handlers in template will handle the rest
  }  /**
   * Handle video loaded and ready to play
   */
  onVideoLoaded(): void {
    this.debugLog('Video loaded and ready');
    console.log(`[PRELOAD] onVideoLoaded called for video: ${this.currentVideo?.id}`);
    this.slideshowService.onVideoLoadComplete();

    // Mark video as seen when it loads
    if (this.currentVideo) {
      this.seenPersistenceService.markVideoAsSeen(this.currentVideo.id);
    }

    // Apply saved volume and mute settings via service
    if (this.videoElement) {
      const persistenceEnabled = this.getVolumePersistenceEnabled();
      this.volumeService.applySavedVolumeSettings(this.videoElement.nativeElement, persistenceEnabled);
    }

    // Prime the edge progress bar for this video
    this.onVideoTimeUpdate();

    // Start preloading next videos after current video is loaded
    console.log(`[PRELOAD] About to start preloading after ${this.currentVideo?.id} loaded`);
    this.mediaPreloadService.startPreloading(
      this.currentVideo,
      () => this.slideshowService.getVideos(),
      () => this.slideshowService.getCurrentIndex(),
    );

    // Auto-play behavior - only resume if the user has not explicitly paused
    const hasVideoElement = !!this.videoElement;

    if (hasVideoElement) {
      if (this.isMobile && !this.shouldAutoPlay) {
        // On mobile, only require manual interaction for the very first video
        this.debugLog('Mobile - waiting for user interaction for first video');
        // We'll rely on the video click handler or manual controls for the first video
      } else if (!this.slideshowService.isUserPaused()) {
        // On desktop (always autoplay), or on mobile after first interaction,
        // but only if the user has not explicitly paused
        setTimeout(() => {
          this.playVideo();
        }, 100);
      }
    }
  }

  /**
   * Handle video play event
   */
  onVideoPlay(): void {
    console.log('Video started playing');
    this.isPlaying = true;
    this.slideshowService.start(); // Keep slideshow state in sync

    // Sample playback position for the edge progress bar while playing
    this.startVideoProgress();

    // Initialize auto-loop if conditions are met
    this.initializeAutoLoop();
  }
  /**
   * Handle video pause event
   */
  onVideoPause(): void {
    console.log('Video paused');
    this.isPlaying = false;

    // Freeze the edge progress bar where playback stopped
    this.stopProgressTicker();

    // Only mark as user-paused if the video hasn't ended naturally.
    // When a video reaches its end the browser fires both a pause and an
    // ended event. We must not treat that automatic pause as a user action,
    // otherwise the next video in the slideshow will not autoplay.
    if (this.videoElement && !this.videoElement.nativeElement.ended) {
      this.slideshowService.pause(); // Keep slideshow state in sync
    }

    // Pause auto-loop monitoring but don't reset
    if (this.autoLoopTimer) {
      clearInterval(this.autoLoopTimer);
      this.autoLoopTimer = null;
    }
  }

  /**
   * Handle keyboard shortcuts for video control
   */
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    // Only handle keyboard events if we have media and the target is not an input field
    if (!this.currentVideo) return;
    
    // Check if we have the appropriate element for the current media type
    const isVideo = this.currentVideo.contentType === 'video';
    const isImage = this.currentVideo.contentType === 'image';
    
    if (isVideo && !this.videoElement) return;
    if (isImage && !this.imageElement) return;

    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

    // Skip spacebar handling if the video element has focus (let browser handle it natively)
    if (event.key === ' ' && isVideo && target === this.videoElement.nativeElement) return;

    // Skip spacebar handling if the image element has focus (let keyup.space binding handle it)
    if (event.key === ' ' && isImage && target === this.imageElement.nativeElement) return;

    // Allow keyboard controls in all modes (windowed, full window, native fullscreen)
    switch (event.key.toLowerCase()) {
      case ' ':
      case 'k':
        // Spacebar or K: Toggle play/pause
        event.preventDefault();
        this.togglePlayPause();
        break;

      case 'arrowleft':
      case 'a':
        // Left arrow or A: Previous video
        event.preventDefault();
        this.previousVideo();
        break;

      case 'arrowright':
      case 'd':
        // Right arrow or D: Next video
        event.preventDefault();
        this.nextVideo();
        break;

      case 'home':
      case 'h':
        // Home key or H: Reset to first video
        event.preventDefault();
        this.resetToFirst();
        break;

      case 'pageup':
        // Page Up: Go back 10 videos
        event.preventDefault();
        this.goBack10();
        break;

      case 'pagedown':
        // Page Down: Go forward 10 videos
        event.preventDefault();
        this.goForward10();
        break;

      case 'arrowup':
        // Up arrow: Volume up (video only)
        if (isVideo) {
          event.preventDefault();
          this.adjustVolume(0.1);
        }
        break;

      case 'arrowdown':
        // Down arrow: Volume down (video only)
        if (isVideo) {
          event.preventDefault();
          this.adjustVolume(-0.1);
        }
        break;

      case 'm':
        // M: Mute/unmute (video only)
        if (isVideo) {
          event.preventDefault();
          this.toggleMute();
        }
        break;

      case 'f':
        // F: Toggle native fullscreen
        event.preventDefault();
        this.toggleNativeFullscreen();
        break;

      case 'r':
        // R: Restart video/image
        event.preventDefault();
        if (isVideo) {
          this.restartVideo();
        } else if (isImage) {
          this.restartImage();
        }
        break;

      case ',':
        // Comma: Step backward 5 seconds (video only)
        if (isVideo) {
          event.preventDefault();
          this.seekRelative(-5);
        }
        break;

      case '.':
        // Period: Step forward 5 seconds (video only)
        if (isVideo) {
          event.preventDefault();
          this.seekRelative(5);
        }
        break;

      case '0':
      case '1':
      case '2':
      case '3':
      case '4':
      case '5':
      case '6':
      case '7':
      case '8':
      case '9': {
        // Number keys: Seek to percentage of video (video only)
        if (isVideo) {
          event.preventDefault();
          const percentage = parseInt(event.key) * 10;
          this.seekToPercentage(percentage);
        }
        break;
      }      case 'w':
        // W: Toggle full window mode
        event.preventDefault();
        this.toggleFullWindowMode();
        break;

      case 'l':
        // L: Toggle current video loop
        event.preventDefault();
        this.toggleLoopCurrent();
        break;      case 'escape':
        // Escape: Exit fullscreen modes
        event.preventDefault();
        if (this.isNativeFullscreen) {
          this.exitNativeFullscreen();
        } else if (this.isFullWindowMode) {
          this.toggleFullWindowMode();
        }
        break;

      case 'e':
        // E: Open current video on e621 in new tab
        event.preventDefault();
        this.openOnE621();
        break;      case 's':
        // S: Favorite current video (if favorites enabled)
        event.preventDefault();
        if (this.favoritesEnabled) {
          this.toggleFavorite();
        }
        break;

      case 'c':
        // C: Toggle video controls visibility (but NOT if Ctrl or Meta is pressed)
        if (!event.ctrlKey && !event.metaKey) {
          event.preventDefault();
          this.toggleHtml5Controls();
        }
        break;
    }
  }
  /**
   * Adjust video volume
   */
  adjustVolume(delta: number): void {
    if (this.videoElement) {
      const video = this.videoElement.nativeElement;
      video.volume = Math.max(0, Math.min(1, video.volume + delta));
      console.log(`Volume adjusted to: ${Math.round(video.volume * 100)}%`);

      // Save the new volume setting via service
      this.volumeService.saveVolume(video.volume, this.getVolumePersistenceEnabled());
    }
  }
  /**
   * Toggle mute/unmute
   */
  toggleMute(): void {
    if (this.videoElement) {
      const video = this.videoElement.nativeElement;
      video.muted = !video.muted;
      console.log(`Video ${video.muted ? 'muted' : 'unmuted'}`);

      // Show toast notification
      this.toastService.showToast(video.muted ? 'Muted' : 'Unmuted');

      // Save the new mute setting via service
      this.volumeService.saveMuteState(video.muted, this.getVolumePersistenceEnabled());
    }
  }

  /**
   * Get current mute state
   */
  get isMuted(): boolean {
    if (this.videoElement) {
      return this.videoElement.nativeElement.muted;
    }
    return false;
  }

  /**
   * Toggle fullscreen
   */
  toggleFullscreen(): void {
    if (this.videoElement) {
      const video = this.videoElement.nativeElement;
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        video.requestFullscreen().catch((error) => {
          console.error('Error entering fullscreen:', error);
        });
      }
    }
  }

  /**
   * Toggle native fullscreen mode
   */
  toggleNativeFullscreen(): void {
    this.fullscreenService.toggleNativeFullscreen();
  }

  /**
   * Exit native fullscreen mode
   */
  exitNativeFullscreen(): void {
    this.miniControlsMinimized = false;
    this.fullscreenService.exitNativeFullscreen();
  }

  /**
   * Restart current video
   */
  restartVideo(): void {
    if (this.videoElement) {
      const video = this.videoElement.nativeElement;

      // Reset auto-loop state since we're restarting
      this.resetAutoLoop();

      video.currentTime = 0;
      if (!this.isPlaying) {
        this.playVideo();
      } else {
        // If already playing, reinitialize auto-loop
        this.initializeAutoLoop();
      }
      console.log('Video restarted');
    }
  }

  /**
   * Restart current image (restart display timer)
   */
  restartImage(): void {
    if (this.currentVideo?.contentType === 'image') {
      // Stop current timer and restart
      this.stopImageTimer();
      
      if (this.isPlaying) {
        // If already playing, restart the timer
        this.startImageTimer();
      } else {
        // If paused, start playing which will start the timer
        this.playImage();
      }
      console.log('Image restarted');
    }
  }

  /**
   * Seek relative to current position
   */
  seekRelative(seconds: number): void {
    if (this.videoElement) {
      const video = this.videoElement.nativeElement;
      video.currentTime = Math.max(
        0,
        Math.min(video.duration || 0, video.currentTime + seconds)
      );

      // Update auto-loop tracking after seek
      if (this.isAutoLoopActive) {
        this.autoLoopLastCurrentTime = video.currentTime;
      }

      console.log(
        `Seeked ${seconds > 0 ? 'forward' : 'backward'} ${Math.abs(
          seconds
        )} seconds`
      );
    }
  }
  /**
   * Seek to percentage of video duration
   */
  seekToPercentage(percentage: number): void {
    if (this.videoElement) {
      const video = this.videoElement.nativeElement;
      if (video.duration) {
        video.currentTime = (video.duration * percentage) / 100;

        // Update auto-loop tracking after seek
        if (this.isAutoLoopActive) {
          this.autoLoopLastCurrentTime = video.currentTime;
        }

        console.log(`Seeked to ${percentage}% of video`);
      }
    }
  }

  /**
   * Toggle full window mode
   */
  toggleFullWindowMode(): void {
    this.fullscreenService.toggleFullWindowMode();
  }
  /**
   * Exit full window mode
   */
  exitFullWindow(): void {
    this.miniControlsMinimized = false;
    this.fullscreenService.exitFullWindow();
  }

  /**
   * Handle close button touch for mobile devices
   */
  onCloseButtonTouch(event: Event): void {
    this.fullscreenService.onCloseButtonTouch(event);
  }

  /**
   * Toggle mini controls minimized state
   */
  toggleMiniControlsMinimized(): void {
    this.miniControlsMinimized = !this.miniControlsMinimized;
  }  /**
   * Play the current video
   */  playVideo(): void {
    // Mark that user has interacted (for mobile autoplay tracking)
    this.hasUserInteracted = true;
    this.shouldAutoPlay = true; // Enable auto-play for subsequent videos

    if (this.videoElement) {
      const video = this.videoElement.nativeElement;

      // Check if video is ready to play
      if (video.readyState < 2) {
        // Wait for video to be ready
        const onLoadedData = () => {
          video.removeEventListener('loadeddata', onLoadedData);
          this.attemptPlay(video);
        };
        video.addEventListener('loadeddata', onLoadedData);
        return;
      }

      this.attemptPlay(video);
    }
  }/**
   * Attempt to play video with proper error handling for mobile
   */
  private attemptPlay(video: HTMLVideoElement): void {
    this.debugLog('Attempting to play video...');
    video.play().then(() => {
      this.debugLog('Video play() promise resolved successfully');
    }).catch((error) => {      this.debugLog(`Play error: ${error.name} - ${error.message}`);

      // Handle different types of play errors
      if (error.name === 'NotAllowedError') {
        // Autoplay was prevented - this is common on mobile
        if (this.isMobile) {
          this.slideshowService.onVideoError('Tap the video or play button to start');
        } else {
          this.slideshowService.onVideoError('Click the video to start playback');
        }
      } else if (error.name === 'NotSupportedError') {
        // Video format not supported
        this.slideshowService.onVideoError('Video format not supported');
      } else {
        // Generic playback error
        this.slideshowService.onVideoError('Failed to play video');
      }
    });
  }

  /**
   * Pause the current video
   */
  pauseVideo(): void {
    if (this.videoElement) {
      this.videoElement.nativeElement.pause();
    }
  }

  /**
   * Play the current image (start/resume timer)
   */
  playImage(): void {
    this.hasUserInteracted = true;
    this.shouldAutoPlay = true;
    this.isPlaying = true;

    // Start image timer for both static images and animated GIFs
    if (this.currentVideo) {
      this.startImageTimer();
    }
  }

  /**
   * Pause the current image (stop timer)
   */
  pauseImage(): void {
    this.isPlaying = false;
    this.stopImageTimer();
  }
  /**
   * Toggle play/pause
   */
  togglePlayPause(): void {
    // Mark that user has interacted (for mobile autoplay tracking)
    this.hasUserInteracted = true;

    if (this.currentVideo?.contentType === 'image') {
      // For images, play/pause controls the slideshow timer
      if (this.isPlaying) {
        this.pauseImage();
        this.slideshowService.pause();
      } else {
        this.playImage();
        this.slideshowService.start();
      }
    } else {
      // For videos, use existing logic
      if (this.isPlaying) {
        this.pauseVideo();
        this.slideshowService.pause();
      } else {
        this.playVideo();
        this.slideshowService.start();
      }
    }
  }  /**
   * Go to next video
   */  nextVideo(): void {
    // Mark that user has interacted (for mobile autoplay tracking)
    this.hasUserInteracted = true;
    this.shouldAutoPlay = true; // Enable auto-play for subsequent videos
    this.slideshowService.next();
  }

  /**
   * Go to previous video
   */
  previousVideo(): void {
    // Mark that user has interacted (for mobile autoplay tracking)
    this.hasUserInteracted = true;
    this.slideshowService.previous();
  }

  /**
   * Go back 10 videos
   */
  goBack10(): void {
    this.slideshowService.goBack(10);
  }

  /**
   * Go forward 10 videos
   */
  goForward10(): void {
    this.slideshowService.goForward(10);
  }
  /**
   * Reset to the first video in the slideshow
   */
  resetToFirst(): void {
    this.slideshowService.goToFirst();
  }

  /**
   * Open current video on e621 in a new tab
   */
  openOnE621(): void {
    if (!this.currentVideo) return;

    const e621Url = `https://e621.net/posts/${this.currentVideo.id}`;
    window.open(e621Url, '_blank');
    console.log(`Opening e621 post: ${e621Url}`);
  }

  /**
   * Handle video element loaded
   */
  onVideoElementLoaded(): void {
    if (this.currentVideo) {
      this.loadVideo();
    }
  }

  /**
   * Get video info for display
   */
  getVideoInfo(): string {
    if (!this.currentVideo) return '';

    const isImage = this.currentVideo.contentType === 'image';
    let info = `${this.currentVideo.width}×${this.currentVideo.height}`;

    if (isImage) {
      // For images, show file format and size
      info += ` • ${this.currentVideo.fileExt.toUpperCase()}`;
      if (this.currentVideo.isAnimated) {
        info += ' (Animated)';
      }
    } else {
      // For videos, show duration
      const duration = this.currentVideo.duration
        ? ` • ${Math.round(this.currentVideo.duration)}s`
        : '';
      info += duration;
    }

    return info;
  }
  /**
   * Get position info for display
   */
  getPositionInfo(): string {
    let info = `${this.position.current} / ${this.position.total}`;
    if (this.imageCountdownSeconds !== null) {
      info += ` (${this.imageCountdownSeconds}s)`;
    }
    return info;
  }

  /**
   * Download the current video file
   */
  async downloadVideo(): Promise<void> {
    if (!this.currentVideo) return;

    const fileName = `e621_${this.currentVideo.id}.${this.currentVideo.fileExt}`;

    try {
      // Try fetch approach first (might bypass some CORS restrictions)
      const response = await fetch(this.currentVideo.url, {
        method: 'GET',
        mode: 'cors',
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        console.log(`Successfully downloaded: ${fileName}`);
        return;
      }
    } catch (error) {
      console.log('Fetch download failed, trying fallback method:', error);
    }

    // Fallback: Direct link approach
    // This might open in browser instead of downloading due to CORS
    const a = document.createElement('a');
    a.href = this.currentVideo.url;
    a.download = fileName;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    console.log(`Fallback download attempt: ${fileName} (may open in browser due to CORS)`);
  }
  /**
   * Clear error message
   */
  clearError(): void {
    this.error = null;
  }
  /**
   * Toggle loop current video mode
   */
  toggleLoopCurrent(): void {
    this.isLoopingCurrent = !this.isLoopingCurrent;
    console.log(`Loop current video: ${this.isLoopingCurrent ? 'enabled' : 'disabled'}`);

    // Manual loop toggle overrides auto-loop
    if (this.isLoopingCurrent) {
      this.stopAutoLoop();
    } else {
      // If turning off manual loop, reinitialize auto-loop if enabled
      this.initializeAutoLoop();
    }

    // If we're enabling loop mode and video is playing, ensure slideshow is stopped
    if (this.isLoopingCurrent && this.isPlaying) {
      this.slideshowService.stop();
    }
    // If we're disabling loop mode and video is playing, restart slideshow
    else if (!this.isLoopingCurrent && this.isPlaying) {
      this.slideshowService.start();
    }
  }

  /**
   * Initialize auto-loop for the current video
   */
  private initializeAutoLoop(): void {
    // Don't start auto-loop if manual loop is active
    if (this.isLoopingCurrent) {
      return;
    }

    const settings = this.autoLoopService.getSettings();
    if (!settings.enabled || !this.currentVideo || !this.videoElement) {
      return;
    }

    this.autoLoopStartTime = Date.now();
    this.autoLoopTotalPlaytime = 0;
    this.autoLoopLastCurrentTime = this.videoElement.nativeElement.currentTime;
    this.isAutoLoopActive = true;

    // Set up loop monitoring
    this.startAutoLoopMonitoring();
  }

  /**
   * Start monitoring auto-loop progress
   */
  private startAutoLoopMonitoring(): void {
    if (this.autoLoopTimer) {
      clearInterval(this.autoLoopTimer);
    }

    this.autoLoopTimer = window.setInterval(() => {
      this.updateAutoLoopProgress();
    }, 100); // Check every 100ms
  }

  /**
   * Update auto-loop progress and handle duration threshold
   */
  private updateAutoLoopProgress(): void {
    if (!this.isAutoLoopActive || !this.isPlaying || !this.videoElement) {
      return;
    }

    const settings = this.autoLoopService.getSettings();
    const video = this.videoElement.nativeElement;
    const currentTime = video.currentTime;

    // Handle video looping (currentTime reset to 0)
    if (currentTime < this.autoLoopLastCurrentTime) {
      // Video looped - add the remaining time from previous loop
      this.autoLoopTotalPlaytime += (video.duration - this.autoLoopLastCurrentTime);
      this.autoLoopLastCurrentTime = 0;
    }

    // Add the time progressed since last check
    const timeDelta = currentTime - this.autoLoopLastCurrentTime;
    if (timeDelta > 0) {
      this.autoLoopTotalPlaytime += timeDelta;
      this.autoLoopLastCurrentTime = currentTime;
    }

    // Check if we've reached the threshold
    if (this.autoLoopTotalPlaytime >= settings.duration) {
      // Threshold reached - disable auto-loop but let current playback complete
      this.stopAutoLoop();

      console.log(`Auto-loop completed after ${this.autoLoopTotalPlaytime.toFixed(1)}s - loop disabled, video will complete naturally`);

      return;
    }
  }

  /**
   * Stop auto-loop functionality
   */
  private stopAutoLoop(): void {
    this.isAutoLoopActive = false;

    if (this.autoLoopTimer) {
      clearInterval(this.autoLoopTimer);
      this.autoLoopTimer = null;
    }
  }

  /**
   * Reset auto-loop state (called on video change)
   */
  private resetAutoLoop(): void {
    this.stopAutoLoop();
    this.autoLoopStartTime = 0;
    this.autoLoopTotalPlaytime = 0;
    this.autoLoopLastCurrentTime = 0;
  }
  /**
   * Toggle video controls visibility
   */
  toggleControlsVisibility(): void {
    this.showControls = !this.showControls;
    console.log(`All video controls (UI + HTML5): ${this.showControls ? 'shown' : 'hidden'}`);
  }

  /**
   * Toggle HTML5 video controls visibility only
   */
  toggleHtml5Controls(): void {
    this.showHtml5Controls = !this.showHtml5Controls;
    console.log(`HTML5 video controls: ${this.showHtml5Controls ? 'shown' : 'hidden'}`);
  }/**
   * Handle video ended event from template
   */  onVideoEnded(): void {
    this.debugLog('Video ended (template event)');

    if (this.isLoopingCurrent) {
      // Native HTML5 loop attribute handles the restart automatically
      // We just need to maintain the playing state for UI consistency
      this.debugLog('Video will loop automatically via HTML5 loop attribute');
      // Don't set isPlaying = false since the video will restart automatically
    } else if (this.isAutoLoopActive) {
      // Auto-loop is handling the looping - video should restart automatically
      this.debugLog('Video will loop automatically via auto-loop');
      // Video should restart automatically due to shouldLoop getter returning true
    } else {
      // Normal behavior: advance to next video
      this.isPlaying = false;
      // When video ends naturally and auto-advances, we should continue autoplay
      this.shouldAutoPlay = true;
      this.debugLog('Video ended - setting shouldAutoPlay = true for next video');
      this.slideshowService.onVideoEnded();
    }
  }/**
   * Handle video error event from template
   */
  onVideoError(event: Event): void {
    const target = event.target as HTMLVideoElement;
    const error = target.error;

    console.error('Video error (template event):', event, error);

    // Reset auto-loop on error
    this.resetAutoLoop();

    let errorMessage = 'Failed to load video';
    if (error) {
      switch (error.code) {
        case error.MEDIA_ERR_ABORTED:
          errorMessage = 'Video loading was aborted';
          break;
        case error.MEDIA_ERR_NETWORK:
          errorMessage = 'Network error while loading video';
          break;
        case error.MEDIA_ERR_DECODE:
          errorMessage = 'Video decoding error';
          break;
        case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
          errorMessage = 'Video format not supported';
          break;
        default:
          errorMessage = 'Unknown video error';
      }
    }

    this.slideshowService.onVideoError(errorMessage);
  }

  /**
   * Handle image loaded event
   */
  onImageLoaded(): void {
    console.log(`[IMAGE] Image loaded for: ${this.currentVideo?.id} (animated: ${this.currentVideo?.isAnimated || false})`);
    this.slideshowService.onVideoLoadComplete();

    // Start image display timer only if the user has not explicitly paused
    if (this.currentVideo && !this.slideshowService.isUserPaused()) {
      this.startImageTimer();
      this.isPlaying = true;
    }
  }

  /**
   * Handle image error event
   */
  onImageError(event: Event): void {
    const target = event.target as HTMLImageElement;
    console.error('Image error:', event, target);

    let errorMessage = 'Failed to load image';
    if (target.src) {
      errorMessage = `Failed to load image: ${target.src}`;
    }

    this.slideshowService.onVideoError(errorMessage);
  }

  /**
   * Handle image click event
   */
  onImageClick(): void {
    const currentTime = Date.now();
    const isDoubleClick = currentTime - this.lastClickTime < this.DOUBLE_CLICK_DELAY;
    this.lastClickTime = currentTime;

    // Handle double-click for favorite action in fullscreen with hidden controls
    if (isDoubleClick &&
        (this.isFullWindowMode || this.isNativeFullscreen) &&
        !this.showControls &&
        this.favoritesEnabled &&
        this.currentVideo) {
      // Toggle favorite first, then show appropriate message
      const wasFavorited = this.isFavorited;
      this.toggleFavorite();
      this.toastService.showToast(wasFavorited ? 'Removed from Favorites' : 'Added to Favorites');
      return;
    }

    // In fullscreen modes, toggle controls or handle favorite
    if (this.isFullWindowMode || this.isNativeFullscreen) {
      this.handleFullscreenImageClick(isDoubleClick);
    } else {
      // In windowed mode, just toggle play/pause (pause image timer)
      this.togglePlayPause();
    }
  }

  /**
   * Start timer for image display (static images and animated GIFs)
   */
  private startImageTimer(): void {
    if (this.imageTimer) {
      clearTimeout(this.imageTimer);
    }
    this.stopImageCountdown();

    // Get image settings from search component
    const imageSettings = this.searchComponent?.getImageSettings();
    
    const settingDuration = imageSettings?.imageDisplayDuration
      ? imageSettings.imageDisplayDuration * 1000
      : this.DEFAULT_IMAGE_DURATION;

    let duration: number;

    if (this.currentVideo?.isAnimated) {
      // Animated GIFs follow the "Animated GIF behavior" setting
      const gifBehavior = imageSettings?.animatedGifBehavior || 'once';

      if (gifBehavior === 'loop') {
        // Loop indefinitely: no timer at all, the GIF keeps playing until the user navigates
        console.log(`[IMAGE] GIF behavior 'loop': no auto-advance timer for GIF ${this.currentVideo.id}`);
        return;
      }

      if (gifBehavior === 'timed' || !this.currentVideo.duration) {
        // Use the configured display duration (the GIF keeps looping until it elapses)
        duration = settingDuration;
        console.log(`[IMAGE] GIF behavior '${gifBehavior}': using display duration ${duration}ms for GIF ${this.currentVideo.id}`);
      } else {
        // Play once: advance after a single loop of the GIF
        duration = this.currentVideo.duration * 1000;
        console.log(`[IMAGE] GIF behavior 'once': using actual GIF duration ${this.currentVideo.duration}s (${duration}ms) for GIF ${this.currentVideo.id}`);
      }
    } else {
      // For static images, use user setting or default
      duration = settingDuration;
    }

    this.startImageCountdown(duration);
    this.startImageProgress(duration);

    this.imageTimer = setTimeout(() => {
      if (this.currentVideo && this.currentVideo.contentType === 'image') {
        // Check if auto-advance is enabled
        if (imageSettings?.imageAutoAdvance !== false) {
          // Simulate video ended event for images (both static and animated)
          this.onImageDisplayComplete();
        }
      }
    }, duration);
  }

  /**
   * Stop image timer
   */
  private stopImageTimer(): void {
    if (this.imageTimer) {
      clearTimeout(this.imageTimer);
      this.imageTimer = null;
    }
    this.stopImageCountdown();
    this.stopProgressTicker();
  }

  /**
   * Start a visible countdown that ticks every second alongside the image timer
   */
  private startImageCountdown(durationMs: number): void {
    this.imageCountdownSeconds = Math.ceil(durationMs / 1000);
    this.imageCountdownInterval = setInterval(() => {
      if (this.imageCountdownSeconds !== null && this.imageCountdownSeconds > 0) {
        this.imageCountdownSeconds--;
        if (this.imageCountdownSeconds === 0 && this.imageCountdownInterval !== null) {
          clearInterval(this.imageCountdownInterval);
          this.imageCountdownInterval = null;
        }
      }
    }, 1000) as unknown as number;
  }

  /**
   * Stop the visible image countdown
   */
  private stopImageCountdown(): void {
    if (this.imageCountdownInterval !== null) {
      clearInterval(this.imageCountdownInterval);
      this.imageCountdownInterval = null;
    }
    this.imageCountdownSeconds = null;
  }

  /**
   * Drive the edge progress bar for images/GIFs from the display timer.
   * Progress simply tracks the running timer - a GIF set to loop forever has
   * no timer and therefore no progress bar.
   */
  private startImageProgress(durationMs: number): void {
    this.stopProgressTicker();
    this.imageProgressDuration = durationMs;
    this.imageProgressStart = Date.now();
    this.mediaProgress = 0;

    this.progressInterval = setInterval(() => {
      const elapsed = Date.now() - this.imageProgressStart;
      this.mediaProgress = Math.min(1, elapsed / this.imageProgressDuration);
      if (this.mediaProgress >= 1) {
        this.stopProgressTicker();
      }
    }, this.PROGRESS_TICK_MS) as unknown as number;
  }

  /**
   * Sample video position on the same cadence as images. The native
   * 'timeupdate' event only fires ~4x a second, which makes the bar step;
   * polling at PROGRESS_TICK_MS keeps it in step with the CSS transition.
   */
  private startVideoProgress(): void {
    this.stopProgressTicker();
    this.progressInterval = setInterval(() => {
      this.onVideoTimeUpdate();
    }, this.PROGRESS_TICK_MS) as unknown as number;
  }

  /**
   * Stop ticking progress, leaving the bar frozen where it is
   */
  private stopProgressTicker(): void {
    if (this.progressInterval !== null) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  /**
   * Reset the edge progress bar (new media)
   */
  private resetMediaProgress(): void {
    this.stopProgressTicker();
    this.imageProgressDuration = 0;
    this.imageProgressStart = 0;
    this.mediaProgress = 0;
  }

  /**
   * Keep the edge progress bar in sync with video playback
   */
  onVideoTimeUpdate(): void {
    if (!this.videoElement) return;
    const video = this.videoElement.nativeElement;
    if (!video.duration || !isFinite(video.duration)) {
      this.mediaProgress = 0;
      return;
    }
    this.mediaProgress = Math.min(1, Math.max(0, video.currentTime / video.duration));
  }

  /**
   * Progress of the current media as a percentage (edge progress bar width)
   */
  get mediaProgressPercent(): number {
    return Math.round(this.mediaProgress * 1000) / 10;
  }

  /**
   * CSS transition duration for the progress fill, kept equal to the sampling
   * interval so each step finishes exactly as the next sample arrives
   */
  get mediaProgressTransition(): string {
    return `${this.PROGRESS_TICK_MS}ms`;
  }

  /**
   * The native video scrubber is the only other on-screen readout of playback
   * position. Images never get one - in fullscreen even the countdown text is
   * hidden - so they always fall back to the edge bar.
   */
  get hasNativeProgressUi(): boolean {
    return this.currentVideo?.contentType === 'video' && this.showHtml5Controls;
  }

  /**
   * Whether the current media has something to track: videos always do,
   * images only while a display timer is running.
   */
  get hasMediaProgress(): boolean {
    if (!this.currentVideo) return false;
    if (this.currentVideo.contentType === 'video') return true;
    return this.imageProgressDuration > 0;
  }

  /**
   * Show the window-edge progress bar in fullscreen once controls are hidden
   */
  get showEdgeProgressBar(): boolean {
    if (!this.isFullWindowMode && !this.isNativeFullscreen) return false;
    if (!this.hasMediaProgress) return false;
    if (this.miniControlsMinimized || !this.showControls) return true;
    return !this.hasNativeProgressUi;
  }

  /**
   * Reset image zoom and pan to default state
   */
  private resetImageZoom(): void {
    this.imageScale = 1;
    this.imageTranslateX = 0;
    this.imageTranslateY = 0;
    this.isPinching = false;
    this.isPanning = false;
    this.clearPinchCooldown();
  }

  /**
   * Clear pinch cooldown state and timer
   */
  private clearPinchCooldown(): void {
    this.pinchCooldown = false;
    if (this.pinchCooldownTimer) {
      clearTimeout(this.pinchCooldownTimer);
      this.pinchCooldownTimer = null;
    }
  }

  /**
   * Whether pinch-to-zoom is enabled for images (reads from settings)
   */
  get isPinchZoomEnabled(): boolean {
    return this.searchComponent?.getImageSettings()?.pinchZoomEnabled === true;
  }

  /**
   * CSS transform string for the image element when zoom/pan is active
   */
  get imageTransform(): string {
    if (this.imageScale === 1 && this.imageTranslateX === 0 && this.imageTranslateY === 0) {
      return '';
    }
    return `scale(${this.imageScale}) translate(${this.imageTranslateX / this.imageScale}px, ${this.imageTranslateY / this.imageScale}px)`;
  }

  /**
   * Handle image display completion (equivalent to video ended)
   */
  private onImageDisplayComplete(): void {
    console.log('Image display completed');
    
    if (this.isLoopingCurrent) {
      // If looping current, restart the timer
      this.startImageTimer();
    } else {
      // Normal behavior: advance to next
      this.isPlaying = false;
      this.shouldAutoPlay = true;
      this.slideshowService.onVideoEnded();
    }
  }

  /**
   * Handle fullscreen image click (similar to video click).
   *
   * Takes the double-click verdict from the caller rather than recomputing it:
   * onImageClick() has already stamped lastClickTime, so measuring against it
   * again here would always look like a double click and every single click
   * would toggle the favorite.
   */
  private handleFullscreenImageClick(isDoubleClick: boolean): void {
    if (isDoubleClick) {
      // Double click - toggle favorite
      if (this.favoritesEnabled) {
        this.toggleFavorite();
      }
    } else {
      // Single click - toggle controls
      this.showControls = !this.showControls;
    }
  }

  /**
   * Handle tag click - append tag to search and execute
   */
  onTagClick(tag: string): void {
    if (this.searchComponent) {
      console.log('Tag clicked:', tag);
      this.searchComponent.appendTagToSearch(tag);
    }
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Format duration for display
   */
  formatDuration(seconds: number | null): string {
    if (!seconds) return 'Unknown';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  /**
   * Format date for display
   */  formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      const timeOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], timeOptions);
    } catch {
      return 'Unknown';
    }
  }

  /**
   * Handle touch start for swipe gestures (works in all views)
   */
  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    if (!this.currentVideo) return;

    // Two-finger pinch-to-zoom (images only, when setting is enabled)
    if (
      event.touches.length === 2 &&
      this.isPinchZoomEnabled &&
      this.currentVideo.contentType === 'image'
    ) {
      const t0 = event.touches[0];
      const t1 = event.touches[1];
      this.pinchStartDist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
      this.pinchStartScale = this.imageScale;
      this.isPinching = true;
      this.isPanning = false;
      event.preventDefault();
      return;
    }

    const touch = event.touches[0];
    this.touchStartY = touch.clientY;
    this.touchStartX = touch.clientX;

    // Single-finger pan when already zoomed in (images only)
    if (
      event.touches.length === 1 &&
      this.isPinchZoomEnabled &&
      this.currentVideo.contentType === 'image' &&
      this.imageScale > 1
    ) {
      this.isPanning = true;
      this.panStartX = touch.clientX;
      this.panStartY = touch.clientY;
      this.panStartTranslateX = this.imageTranslateX;
      this.panStartTranslateY = this.imageTranslateY;
    } else {
      this.isPanning = false;
    }
  }

  /**
   * Handle touch move for pinch-to-zoom and pan gestures
   */
  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (!this.currentVideo) return;

    // Handle ongoing pinch gesture
    if (this.isPinching && event.touches.length === 2) {
      const t0 = event.touches[0];
      const t1 = event.touches[1];
      const currentDist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
      const rawScale = this.pinchStartScale * (currentDist / this.pinchStartDist);
      this.imageScale = Math.min(this.MAX_ZOOM_SCALE, Math.max(1, rawScale));
      // Reset pan when zooming back to 1
      if (this.imageScale === 1) {
        this.imageTranslateX = 0;
        this.imageTranslateY = 0;
      }
      event.preventDefault();
      return;
    }

    // Handle ongoing pan gesture (single finger, while zoomed in)
    if (this.isPanning && event.touches.length === 1 && this.imageScale > 1) {
      const touch = event.touches[0];
      this.imageTranslateX = this.panStartTranslateX + (touch.clientX - this.panStartX);
      this.imageTranslateY = this.panStartTranslateY + (touch.clientY - this.panStartY);
      event.preventDefault();
    }
  }

  /**
   * Handle touch end for swipe gestures (works in all views)
   */
  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    if (!this.currentVideo) return;

    // Finalise a pinch gesture – do not fire swipe navigation
    if (this.isPinching) {
      this.isPinching = false;
      // Snap back to 1 if barely zoomed
      if (this.imageScale < this.ZOOM_SNAP_THRESHOLD) {
        this.imageScale = 1;
        this.imageTranslateX = 0;
        this.imageTranslateY = 0;
      }
      // Start cooldown to prevent accidental navigation right after a pinch
      this.clearPinchCooldown();
      this.pinchCooldown = true;
      this.pinchCooldownTimer = setTimeout(() => {
        this.pinchCooldown = false;
      }, this.PINCH_COOLDOWN_MS);
      return;
    }

    // Finalise a pan gesture – do not fire swipe navigation
    if (this.isPanning) {
      this.isPanning = false;
      return;
    }

    // Block swipe navigation during pinch cooldown or when zoomed in
    if (this.isPinchZoomEnabled && this.currentVideo.contentType === 'image' && (this.imageScale > 1 || this.pinchCooldown)) {
      return;
    }

    const touch = event.changedTouches[0];
    const deltaY = touch.clientY - this.touchStartY;
    const deltaX = touch.clientX - this.touchStartX;

    // Check if this is primarily a vertical swipe (only works in fullscreen modes)
    if (
      (this.isFullWindowMode || this.isNativeFullscreen) &&
      Math.abs(deltaY) > Math.abs(deltaX) &&
      Math.abs(deltaY) > this.minSwipeDistance
    ) {
      event.preventDefault();

      if (deltaY > 0) {
        // Swipe down = previous video
        console.log('Swipe down detected - going to previous video');
        this.previousVideo();
      } else {
        // Swipe up = next video
        console.log('Swipe up detected - going to next video');
        this.nextVideo();
      }
    }
    // Check if this is primarily a horizontal swipe (works in all views)
    else if (
      Math.abs(deltaX) > Math.abs(deltaY) &&
      Math.abs(deltaX) > this.minSwipeDistance
    ) {
      event.preventDefault();

      if (deltaX > 0) {
        // Swipe right = previous video
        console.log('Swipe right detected - going to previous video');
        this.previousVideo();
      } else {
        // Swipe left = next video
        console.log('Swipe left detected - going to next video');
        this.nextVideo();
      }
    }
  }

  /**
   * Handle mouse wheel scroll for navigation (works in all fullscreen modes)
   */
  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    // Allow scroll controls in both windowed fullscreen and native fullscreen
    if (!(this.isFullWindowMode || this.isNativeFullscreen) || !this.currentVideo) return;

    // Prevent default scrolling behavior
    event.preventDefault();

    // Determine scroll direction (inverted controls)
    if (event.deltaY > 0) {
      // Scroll down = next video
      console.log('Scroll down detected - going to next video');
      this.nextVideo();
    } else if (event.deltaY < 0) {
      // Scroll up = previous video
      console.log('Scroll up detected - going to previous video');
      this.previousVideo();
    }
  }
  /**
   * Get whether volume persistence is currently enabled
   */
  private getVolumePersistenceEnabled(): boolean {
    if (this.searchComponent) {
      return this.searchComponent.getPersistenceSettings().persistVolume;
    }
    return true; // Default to enabled if no search component
  }
  /**
   * Handle video volume change event (from native controls)
   */
  onVolumeChange(): void {
    if (this.videoElement) {
      const video = this.videoElement.nativeElement;
      console.log(`Volume changed via native controls: ${Math.round(video.volume * 100)}%, muted: ${video.muted}`);

      // Save both volume and mute state when changed via native controls
      this.volumeService.saveVolume(video.volume, this.getVolumePersistenceEnabled());
      this.volumeService.saveMuteState(video.muted, this.getVolumePersistenceEnabled());
    }
  }

  /**
   * Handle video click - mobile autoplay initialization and double-click for favorites in fullscreen
   */
  onVideoClick(): void {
    const currentTime = Date.now();
    const isDoubleClick = currentTime - this.lastClickTime < this.DOUBLE_CLICK_DELAY;
    this.lastClickTime = currentTime;

    // Handle double-click for favorite action in fullscreen with hidden controls
    if (isDoubleClick &&
        (this.isFullWindowMode || this.isNativeFullscreen) &&
        !this.showControls &&
        this.favoritesEnabled &&
        this.currentVideo) {
      // Toggle favorite first, then show appropriate message
      const wasFavorited = this.isFavorited;
      this.toggleFavorite();
      this.toastService.showToast(wasFavorited ? 'Removed from Favorites' : 'Added to Favorites');
      return;
    }

    // On mobile, if video is not playing and slideshow is playing, try to start playback
    if (this.isMobile && this.slideshowService.getIsPlaying() && !this.isPlaying) {
      this.debugLog('Video clicked - attempting to start playback (mobile)');
      this.playVideo();
    }
    // Otherwise, let native controls handle play/pause
  }  /**
   * Set up mobile debugging to capture console logs
   */
  private setupMobileDebugging(): void {
    const originalConsoleLog = console.log;
    console.log = (...args: unknown[]) => {
      // Call original console.log
      originalConsoleLog.apply(console, args);

      // Only capture logs if debug panel is visible
      if (this.showMobileDebugPanel) {
        const message = args.map(arg =>
          typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
        ).join(' ');
        this.debugLog(message);
      }
    };

    const originalConsoleError = console.error;
    console.error = (...args: unknown[]) => {
      // Call original console.error
      originalConsoleError.apply(console, args);

      // Only capture errors if debug panel is visible
      if (this.showMobileDebugPanel) {
        const message = args.map(arg =>
          typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
        ).join(' ');
        this.debugLog('ERROR: ' + message);
      }
    };

    const originalConsoleWarn = console.warn;
    console.warn = (...args: unknown[]) => {
      // Call original console.warn
      originalConsoleWarn.apply(console, args);

      // Only capture warnings if debug panel is visible
      if (this.showMobileDebugPanel) {
        const message = args.map(arg =>
          typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
        ).join(' ');
        this.debugLog('WARN: ' + message);
      }
    };
  }

  /**
   * Add a debug log entry (for mobile debugging)
   */
  private debugLog(message: string): void {
    const timestamp = new Date().toLocaleTimeString();
    this.mobileDebugLogs.push(`[${timestamp}] ${message}`);

    // Keep only last 50 logs to prevent memory issues
    if (this.mobileDebugLogs.length > 50) {
      this.mobileDebugLogs = this.mobileDebugLogs.slice(-50);
    }
  }

  /**
   * Toggle mobile debug panel
   */
  toggleMobileDebug(): void {
    this.showMobileDebug = !this.showMobileDebug;
  }
  /**
   * Clear mobile debug logs
   */
  clearMobileDebugLogs(): void {
    this.mobileDebugLogs = [];
    this.debugLog('Debug logs cleared');
  }  /**
   * Set up 3-finger tap gesture to show/hide mobile debug panel
   */
  private setupMobileDebugGesture(): void {
    document.addEventListener('touchstart', (event: TouchEvent) => {
      if (event.touches.length === 3) {
        event.preventDefault();
        this.showMobileDebugPanel = !this.showMobileDebugPanel;
        if (this.showMobileDebugPanel) {
          this.showMobileDebug = true;
          this.debugLog('Debug panel activated via 3-finger tap');
        } else {
          this.showMobileDebug = false;
          this.debugLog('Debug panel hidden via 3-finger tap');
        }
      }
    }, { passive: false });
  }

  /**
   * Update favorite status for current video
   */
  private updateFavoriteStatus(): void {
    if (this.currentVideo && this.favoritesEnabled) {
      this.isFavorited = this.favoritesService.isFavorited(this.currentVideo.id);
    } else {
      this.isFavorited = false;
    }
  }  /**
   * Toggle favorite status of current video
   */
  toggleFavorite(): void {
    if (!this.currentVideo || !this.favoritesEnabled || this.isFavoriteLoading) {
      return;
    }

    this.isFavoriteLoading = true;

    if (this.isFavorited) {
      this.favoritesService.removeFromFavorites(this.currentVideo.id).subscribe({
        next: (success) => {
          this.isFavoriteLoading = false;
          if (success) {
            this.isFavorited = false;
            console.log('Removed from favorites:', this.currentVideo!.id);
          }
        },
        error: (error) => {
          this.isFavoriteLoading = false;
          console.error('Error removing from favorites:', error);
        }
      });
    } else {
      this.favoritesService.addToFavorites(this.currentVideo.id).subscribe({
        next: (success) => {
          this.isFavoriteLoading = false;
          if (success) {
            this.isFavorited = true;
            console.log('Added to favorites:', this.currentVideo!.id);
          }
        },
        error: (error) => {
          this.isFavoriteLoading = false;
          console.error('Error adding to favorites:', error);
        }
      });
    }
  }

  /**
   * Escape the HTML-significant characters in uploader-authored text.
   *
   * Descriptions come straight from e621 and can contain arbitrary markup, so
   * this runs before any DText substitution: everything emitted after this
   * point is markup we generated ourselves, and anything the uploader wrote
   * stays inert text.
   */
  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Whether a DText link target is safe to emit as an href.
   * Only absolute http(s) URLs are allowed — javascript:, data:, vbscript:
   * and friends are rendered as plain text instead.
   */
  private isSafeLinkUrl(url: string): boolean {
    // Browsers ignore whitespace and control characters when resolving a
    // scheme, so strip them before testing ("java\tscript:" must not pass).
    const normalized = Array.from(url)
      .filter((ch) => ch.charCodeAt(0) > 32)
      .join('')
      .toLowerCase();
    return /^https?:\/\//.test(normalized);
  }

  /**
   * Whether a DText colour value is safe to emit inside a style attribute.
   * Anything that is not a plain named/hex/rgb/hsl colour is dropped, so a
   * colour value cannot smuggle in extra CSS declarations.
   */
  private isSafeCssColor(value: string): boolean {
    const normalized = value.trim().toLowerCase();
    return /^#[0-9a-f]{3,8}$/.test(normalized)
        || /^[a-z]+$/.test(normalized)
        || /^rgba?\([\d\s.,%]+\)$/.test(normalized)
        || /^hsla?\([\d\s.,%]+\)$/.test(normalized);
  }

  /**
   * Convert e621 DText format to HTML
   * DText is similar to BBCode but with some differences
   */
  convertDTextToHtml(dtext: string): string {
    if (!dtext) return '';

    // Escape before substituting anything — see escapeHtml().
    let html = this.escapeHtml(dtext);

    // Handle line breaks first
    html = html.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    // Bold text: [b]text[/b]
    html = html.replace(/\[b\](.*?)\[\/b\]/gi, '<strong>$1</strong>');

    // Italic text: [i]text[/i]
    html = html.replace(/\[i\](.*?)\[\/i\]/gi, '<em>$1</em>');

    // Underline text: [u]text[/u]
    html = html.replace(/\[u\](.*?)\[\/u\]/gi, '<u>$1</u>');

    // Strikethrough text: [s]text[/s]
    html = html.replace(/\[s\](.*?)\[\/s\]/gi, '<del>$1</del>');

    // Superscript: [sup]text[/sup]
    html = html.replace(/\[sup\](.*?)\[\/sup\]/gi, '<sup>$1</sup>');

    // Subscript: [sub]text[/sub]
    html = html.replace(/\[sub\](.*?)\[\/sub\]/gi, '<sub>$1</sub>');

    // Color text: [color=red]text[/color] — unrecognised colours are dropped
    html = html.replace(/\[color=([^[\]]+)\](.*?)\[\/color\]/gi, (_match, color: string, text: string) =>
      this.isSafeCssColor(color) ? `<span style="color: ${color}">${text}</span>` : text);

    // Size text: [size=20]text[/size]
    html = html.replace(/\[size=(\d+)\](.*?)\[\/size\]/gi, '<span style="font-size: $1px">$2</span>');

    // Links: [url=http://example.com]text[/url] or [url]http://example.com[/url]
    // Non-http(s) targets are rendered as plain text rather than as an anchor.
    html = html.replace(/\[url=([^[\]]+)\](.*?)\[\/url\]/gi, (_match, url: string, text: string) =>
      this.isSafeLinkUrl(url)
        ? `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`
        : text);
    html = html.replace(/\[url\](.*?)\[\/url\]/gi, (_match, url: string) =>
      this.isSafeLinkUrl(url)
        ? `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
        : url);

    // Auto-link URLs that aren't already in [url] tags
    html = html.replace(/(^|[^"'])(https?:\/\/[^\s<>"']+)/gi, '$1<a href="$2" target="_blank" rel="noopener noreferrer">$2</a>');

    // Quotes: [quote]text[/quote] or [quote="username"]text[/quote]
    // The author's quotes are already escaped at this point, so match &quot;
    html = html.replace(/\[quote=&quot;(.+?)&quot;\](.*?)\[\/quote\]/gis, '<blockquote><cite>$1 said:</cite><div>$2</div></blockquote>');
    html = html.replace(/\[quote\](.*?)\[\/quote\]/gis, '<blockquote>$1</blockquote>');

    // Code blocks: [code]text[/code]
    html = html.replace(/\[code\](.*?)\[\/code\]/gis, '<pre><code>$1</code></pre>');

    // Spoilers: [spoiler]text[/spoiler] or [spoiler="title"]text[/spoiler]
    html = html.replace(/\[spoiler=&quot;(.+?)&quot;\](.*?)\[\/spoiler\]/gis, '<details class="spoiler"><summary>$1</summary><div>$2</div></details>');
    html = html.replace(/\[spoiler\](.*?)\[\/spoiler\]/gis, '<details class="spoiler"><summary>Spoiler</summary><div>$1</div></details>');    // Lists: [list] and [*] items
    html = html.replace(/\[list\](.*?)\[\/list\]/gis, (match, content) => {
      const items = content.replace(/\[\*\]([^[]*)/g, '<li>$1</li>');
      return `<ul>${items}</ul>`;
    });

    // Ordered lists: [list=1] or [list=a]
    html = html.replace(/\[list=([1a])\](.*?)\[\/list\]/gis, (match, type, content) => {
      const items = content.replace(/\[\*\]([^[]*)/g, '<li>$1</li>');
      const listType = type === '1' ? 'ol' : 'ol style="list-style-type: lower-alpha"';
      return `<${listType}>${items}</${listType.split(' ')[0]}>`;
    });

    // Thumbnails and images: {{image_id}} or post #123456
    html = html.replace(/\{\{(\d+)\}\}/g, '<a href="https://e621.net/posts/$1" target="_blank" rel="noopener noreferrer">post #$1</a>');
    html = html.replace(/post #(\d+)/gi, '<a href="https://e621.net/posts/$1" target="_blank" rel="noopener noreferrer">post #$1</a>');

    // User mentions: @username
    html = html.replace(/@([a-zA-Z0-9_]+)/g, '<a href="https://e621.net/users/$1" target="_blank" rel="noopener noreferrer">@$1</a>');

    // Convert line breaks to <br> tags
    html = html.replace(/\n/g, '<br>');

    // Clean up multiple consecutive <br> tags
    html = html.replace(/(<br>\s*){3,}/g, '<br><br>');

    return html;  }

  /**
   * Get poster URL with fallback from sample to preview
   */
  getPosterUrl(): string {
    if (!this.currentVideo) return '';

    // Use sample URL if available, not empty, and not null/undefined
    const sampleUrl = this.currentVideo.sampleUrl;
    if (sampleUrl) {
      return sampleUrl;
    }

    // Fall back to preview URL
    const previewUrl = this.currentVideo.previewUrl || '';
    return previewUrl;
  }

  /**
   * Get formatted description HTML
   */
  getFormattedDescription(): SafeHtml {
    if (!this.currentVideo?.description) return '';
    const htmlContent = this.convertDTextToHtml(this.currentVideo.description);
    // Safe to bypass only because convertDTextToHtml() escapes the uploader's
    // text before substituting anything, so every tag below is markup we
    // generated. Do not feed unescaped content through here.
    return this.sanitizer.bypassSecurityTrustHtml(htmlContent);
  }
}
